import { useCallback, useState } from "react";
import { useMutation, useQuery } from "urql";
import type { TermInterviewersQuery as GeneratedTermInterviewersQuery } from "../graphql/generated/graphql";
import {
  AssignInterviewerToTermMutation,
  CreateInterviewerMutation,
  DeleteInterviewerMutation,
  InterviewerQuery,
  InterviewersQuery,
  RemoveInterviewerFromTermMutation,
  TermInterviewersQuery,
  UpdateInterviewerMutation,
} from "../graphql/interviewers.graphql";
import { toUserFacingMessage } from "../lib/graphqlErrorMessage";
import { logger } from "../lib/logger";

export interface Interviewer {
  interviewerId: string;
  email: string;
  name: string;
  isAdmin: boolean;
  isDefault: boolean;
  isKeyInterviewer: boolean;
  createdAt: string | null;
  updatedAt: string | null;
}

/**
 * タームにアサインされたインタビュアー。ターム内での状況を持つ。
 *
 * 手書きせず生成型から取る。サーバの型とずれると件数が undefined になり、
 * 「連携済み（undefinedアカウント）」のような壊れ方をするため。
 */
export type TermInterviewer =
  GeneratedTermInterviewersQuery["termInterviewers"]["nodes"][number];

export interface CreateInterviewerInput {
  email: string;
  name: string;
  isAdmin?: boolean;
  isDefault?: boolean;
  isKeyInterviewer?: boolean;
}

export interface UpdateInterviewerInput {
  interviewerId: string;
  name?: string | null;
  isAdmin?: boolean;
  isDefault?: boolean;
  isKeyInterviewer?: boolean;
}

export function useInterviewers() {
  const [result, reexecute] = useQuery({ query: InterviewersQuery });

  return {
    interviewers: (result.data?.interviewers.nodes ?? []) as Interviewer[],
    loading: result.fetching,
    error: result.error
      ? toUserFacingMessage(result.error, "インタビュアーの取得に失敗しました")
      : null,
    refetch: () => reexecute({ requestPolicy: "network-only" }),
  };
}

export function useTermInterviewers(termId: string | null) {
  const [result, reexecute] = useQuery({
    query: TermInterviewersQuery,
    variables: { termId: termId ?? "" },
    pause: !termId,
  });

  return {
    interviewers: result.data?.termInterviewers.nodes ?? [],
    loading: result.fetching,
    error: result.error
      ? toUserFacingMessage(
          result.error,
          "タームのインタビュアーの取得に失敗しました",
        )
      : null,
    refetch: () => reexecute({ requestPolicy: "network-only" }),
  };
}

export function useInterviewer(interviewerId: string | null) {
  const [result, reexecute] = useQuery({
    query: InterviewerQuery,
    variables: { interviewerId: interviewerId ?? "" },
    pause: !interviewerId,
  });

  return {
    interviewer: (result.data?.interviewer as Interviewer | undefined) ?? null,
    loading: result.fetching,
    error: result.error
      ? toUserFacingMessage(result.error, "インタビュアーの取得に失敗しました")
      : null,
    refetch: () => reexecute({ requestPolicy: "network-only" }),
  };
}

export function useInterviewerMutations() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [, executeCreate] = useMutation(CreateInterviewerMutation);
  const [, executeUpdate] = useMutation(UpdateInterviewerMutation);
  const [, executeDelete] = useMutation(DeleteInterviewerMutation);
  const [, executeAssign] = useMutation(AssignInterviewerToTermMutation);
  const [, executeRemove] = useMutation(RemoveInterviewerFromTermMutation);

  const createInterviewer = useCallback(
    async (data: CreateInterviewerInput): Promise<Interviewer | null> => {
      setLoading(true);
      setError(null);
      try {
        const result = await executeCreate({ input: data });
        if (result.error) throw new Error(result.error.message);
        return (result.data?.createInterviewer as Interviewer) ?? null;
      } catch (err) {
        const message =
          err instanceof Error
            ? err.message
            : "インタビュアーの作成に失敗しました";
        setError(message);
        return null;
      } finally {
        setLoading(false);
      }
    },
    [executeCreate],
  );

  const updateInterviewer = useCallback(
    async (data: UpdateInterviewerInput): Promise<Interviewer | null> => {
      setLoading(true);
      setError(null);
      try {
        const result = await executeUpdate({ input: data });
        if (result.error) throw new Error(result.error.message);
        return (
          (result.data?.updateInterviewer as Interviewer | undefined) ?? null
        );
      } catch (err) {
        const message =
          err instanceof Error
            ? err.message
            : "インタビュアーの更新に失敗しました";
        setError(message);
        return null;
      } finally {
        setLoading(false);
      }
    },
    [executeUpdate],
  );

  const deleteInterviewer = useCallback(
    async (
      interviewerId: string,
    ): Promise<{ ok: true } | { ok: false; message: string }> => {
      setLoading(true);
      setError(null);
      try {
        const result = await executeDelete({ interviewerId });
        if (result.error) throw new Error(result.error.message);
        const payload = result.data?.deleteInterviewer;
        if (payload == null) {
          return { ok: false, message: "インタビュアーの削除に失敗しました" };
        }
        switch (payload.__typename) {
          case "DeleteInterviewerSuccess":
            return { ok: true };
          case "DeleteInterviewerHasUpcomingScheduledInterviewsError":
          case "DeleteInterviewerHasRelatedInterviewsError":
            return { ok: false, message: payload.message };
          default: {
            // backend が Union に新エラー型を足した時に default を経由するため
            // never 割り当てで TS コンパイル時に網羅性を強制する
            const _exhaustive: never = payload;
            logger.error("Unknown deleteInterviewer payload", {
              typename: (_exhaustive as { __typename: string }).__typename,
            });
            return { ok: false, message: "想定外のレスポンスを受け取りました" };
          }
        }
      } catch (err) {
        const message =
          err instanceof Error
            ? err.message
            : "インタビュアーの削除に失敗しました";
        setError(message);
        return { ok: false, message };
      } finally {
        setLoading(false);
      }
    },
    [executeDelete],
  );

  const assignInterviewerToTerm = useCallback(
    async (
      termId: string,
      interviewerId: string,
    ): Promise<{ success: boolean; errorMessage: string | null } | null> => {
      setLoading(true);
      setError(null);
      try {
        const result = await executeAssign({ termId, interviewerId });
        if (result.error) throw new Error(result.error.message);
        const data = result.data?.assignInterviewerToTerm;
        if (!data) return null;
        return {
          success: data.success,
          errorMessage: data.errorMessage,
        };
      } catch (err) {
        const message =
          err instanceof Error
            ? err.message
            : "インタビュアーのアサインに失敗しました";
        setError(message);
        return null;
      } finally {
        setLoading(false);
      }
    },
    [executeAssign],
  );

  const removeInterviewerFromTerm = useCallback(
    async (
      termId: string,
      interviewerId: string,
    ): Promise<{ success: boolean; errorMessage: string | null } | null> => {
      setLoading(true);
      setError(null);
      try {
        const result = await executeRemove({ termId, interviewerId });
        if (result.error) throw new Error(result.error.message);
        const data = result.data?.removeInterviewerFromTerm;
        if (!data) return null;
        return {
          success: data.success,
          errorMessage: data.errorMessage,
        };
      } catch (err) {
        const message =
          err instanceof Error
            ? err.message
            : "インタビュアーの削除に失敗しました";
        setError(message);
        return null;
      } finally {
        setLoading(false);
      }
    },
    [executeRemove],
  );

  return {
    createInterviewer,
    updateInterviewer,
    deleteInterviewer,
    assignInterviewerToTerm,
    removeInterviewerFromTerm,
    loading,
    error,
  };
}
